import React, { useState, useEffect } from 'react';
import {
  Card,
  Table,
  Button,
  Input,
  Select,
  Tag,
  Tree,
  DatePicker,
  Row,
  Col,
  message,
  Tabs,
  Statistic,
  Descriptions,
  Spin,
} from 'antd';
import {
  ReloadOutlined,
  DollarOutlined,
  TeamOutlined,
  SearchOutlined,
  UserOutlined,
} from '@ant-design/icons';
import AuthGuard from '../components/AuthGuard';
import {
  getReferralTree,
  getReferralEarnings,
  getReferralsByPhone,
} from '../services/adminApi';
import type { User } from '../types';
import dayjs from 'dayjs';

const { Search } = Input;
const { RangePicker } = DatePicker;

const roleMap: Record<string, string> = {
  admin: '管理员',
  level1: '一级推荐官',
  level2: '二级推荐官',
  level3: '三级推荐官',
  retail: '用电用户'
};

const roleColorMap: Record<string, string> = {
  admin: 'red',
  level1: 'green',
  level2: 'orange',
  level3: 'purple',
  retail: 'blue'
};

interface ReferralTreeNode {
  id: number;
  phone: string;
  nickname: string;
  role: string;
  status: string;
  referralCode: string;
  createdAt: string;
  children?: ReferralTreeNode[];
  profile?: {
    companyName?: string;
    realName?: string;
  };
}

interface EarningsRecord {
  id: number;
  userId: number;
  type: string;
  periodType: string;
  periodStart: string;
  periodEnd: string;
  baseAmount?: string;
  rate?: string;
  profitAmount: string;
  bonusAmount: string;
  totalAmount: string;
  status: string;
  settledAt?: string;
  createdAt: string;
  user: {
    phone: string;
    nickname: string;
    role: string;
    profile?: {
      companyName?: string;
      realName?: string;
    };
  };
}

const ReferralManagement: React.FC = () => {
  const [treeData, setTreeData] = useState<ReferralTreeNode[]>([]);
  const [earnings, setEarnings] = useState<EarningsRecord[]>([]);
  const [loading, setLoading] = useState(false);
  const [earningsLoading, setEarningsLoading] = useState(false);
  const [userIdFilter, setUserIdFilter] = useState<number | undefined>();
  const [dateRange, setDateRange] = useState<[dayjs.Dayjs, dayjs.Dayjs] | null>(null);
  const [earningsPagination, setEarningsPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });

  // 新增：手机号查询相关状态
  const [searchPhone, setSearchPhone] = useState<string>('');
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchResult, setSearchResult] = useState<any>(null);
  const [referralsList, setReferralsList] = useState<any[]>([]);
  const [referralsPagination, setReferralsPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });
  const [levelFilter, setLevelFilter] = useState<string | undefined>();

  // 获取推荐官层级树
  const fetchReferralTree = async () => {
    setLoading(true);
    try {
      const response = await getReferralTree();
      if (response.data.success) {
        setTreeData(response.data.data);
      }
    } catch (error: any) {
      message.error(error.message || '获取推荐官层级关系失败');
    } finally {
      setLoading(false);
    }
  };

  // 获取推荐官收益
  const fetchEarnings = async (page = 1, pageSize = 10) => {
    setEarningsLoading(true);
    try {
      const response = await getReferralEarnings({
        page,
        pageSize,
        userId: userIdFilter,
        periodStart: dateRange?.[0]?.format('YYYY-MM-DD'),
        periodEnd: dateRange?.[1]?.format('YYYY-MM-DD'),
      });

      if (response.data.success) {
        setEarnings(response.data.data.list);
        setEarningsPagination({
          current: response.data.data.page,
          pageSize: response.data.data.pageSize,
          total: response.data.data.total,
        });
      }
    } catch (error: any) {
      message.error(error.message || '获取推荐官收益失败');
    } finally {
      setEarningsLoading(false);
    }
  };

  // 新增：根据手机号查询被推荐人
  const fetchReferralsByPhone = async (page = 1, pageSize = 10) => {
    if (!searchPhone) {
      message.warning('请输入手机号');
      return;
    }

    setSearchLoading(true);
    try {
      const response = await getReferralsByPhone({
        phone: searchPhone,
        page,
        pageSize,
        level: levelFilter,
      });

      if (response.data.success) {
        setSearchResult(response.data.data.referrer);
        setReferralsList(response.data.data.list);
        setReferralsPagination({
          current: response.data.data.page,
          pageSize: response.data.data.pageSize,
          total: response.data.data.total,
        });
      }
    } catch (error: any) {
      message.error(error.message || '查询失败');
      setSearchResult(null);
      setReferralsList([]);
    } finally {
      setSearchLoading(false);
    }
  };

  useEffect(() => {
    fetchReferralTree();
    fetchEarnings();
  }, [userIdFilter, dateRange]);

  // 递归转换树形数据
  const formatTreeNode = (node: ReferralTreeNode): any => {
    return {
      title: (
        <div>
          <div>
            <strong>{node.nickname || '未设置'}</strong>
            <Tag color={roleColorMap[node.role] || 'blue'} style={{ marginLeft: 8 }}>
              {roleMap[node.role] || node.role}
            </Tag>
          </div>
          <div style={{ fontSize: '12px', color: '#666' }}>
            {node.phone} | {node.referralCode}
          </div>
        </div>
      ),
      key: node.id,
      children: node.children && node.children.length > 0
        ? node.children.map(child => formatTreeNode(child))
        : undefined,
    };
  };

  const treeDataFormatted = treeData.map(node => formatTreeNode(node));

  const earningsColumns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 80,
    },
    {
      title: '用户',
      dataIndex: ['user', 'nickname'],
      key: 'user',
      render: (nickname: string, record: EarningsRecord) => (
        <div>
          <div>{nickname || '未设置'}</div>
          <div style={{ fontSize: '12px', color: '#666' }}>
            {record.user.phone}
          </div>
        </div>
      ),
    },
    {
      title: '角色',
      dataIndex: ['user', 'role'],
      key: 'role',
      render: (role: string) => {
        return <Tag color={roleColorMap[role] || 'blue'}>{roleMap[role] || role}</Tag>;
      },
    },
    {
      title: '分润类型',
      dataIndex: 'type',
      key: 'type',
      render: (type: string) => {
        const typeMap: Record<string, { label: string; color: string }> = {
          basic: { label: '基础分润', color: 'blue' },
          management: { label: '管理分润', color: 'orange' },
          team: { label: '团队分润', color: 'purple' },
        };
        const typeInfo = typeMap[type] || typeMap.basic;
        return <Tag color={typeInfo.color}>{typeInfo.label}</Tag>;
      },
    },
    {
      title: '周期',
      key: 'period',
      render: (_: any, record: EarningsRecord) => (
        <div>
          <div>{record.periodStart}</div>
          <div>至</div>
          <div>{record.periodEnd}</div>
        </div>
      ),
    },
    {
      title: '基础金额',
      dataIndex: 'baseAmount',
      key: 'baseAmount',
      render: (amount: string) => amount ? `¥${parseFloat(amount).toLocaleString()}` : '-',
    },
    {
      title: '分润比例',
      dataIndex: 'rate',
      key: 'rate',
      render: (rate: string) => rate ? `${(parseFloat(rate) * 100).toFixed(2)}%` : '-',
    },
    {
      title: '分润金额',
      dataIndex: 'profitAmount',
      key: 'profitAmount',
      render: (amount: string) => `¥${parseFloat(amount).toLocaleString()}`,
    },
    {
      title: '奖励金额',
      dataIndex: 'bonusAmount',
      key: 'bonusAmount',
      render: (amount: string) => amount ? `¥${parseFloat(amount).toLocaleString()}` : '-',
    },
    {
      title: '总金额',
      dataIndex: 'totalAmount',
      key: 'totalAmount',
      render: (amount: string) => `¥${parseFloat(amount).toLocaleString()}`,
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      render: (status: string) => {
        const statusMap: Record<string, { label: string; color: string }> = {
          pending: { label: '待结算', color: 'warning' },
          confirmed: { label: '已确认', color: 'processing' },
          paid: { label: '已支付', color: 'success' },
        };
        const statusInfo = statusMap[status] || statusMap.pending;
        return <Tag color={statusInfo.color}>{statusInfo.label}</Tag>;
      },
    },
    {
      title: '结算时间',
      dataIndex: 'settledAt',
      key: 'settledAt',
      render: (date: string) => date ? new Date(date).toLocaleString() : '-',
    },
  ];

  const handleEarningsTableChange = (pagination: any) => {
    fetchEarnings(pagination.current, pagination.pageSize);
  };

  // 计算总收益
  const totalEarnings = earnings.reduce((sum, record) => {
    return sum + parseFloat(record.totalAmount);
  }, 0);

  // 被推荐人列表表格列定义
  const referralsColumns = [
    {
      title: '层级',
      dataIndex: 'level',
      key: 'level',
      width: 80,
      render: (level: number) => (
        <Tag color={level === 1 ? 'green' : 'blue'}>
          {level === 1 ? '一级' : '二级'}
        </Tag>
      ),
    },
    {
      title: '用户ID',
      dataIndex: 'id',
      key: 'id',
      width: 80,
    },
    {
      title: '手机号',
      dataIndex: 'phone',
      key: 'phone',
      width: 120,
    },
    {
      title: '昵称',
      dataIndex: 'nickname',
      key: 'nickname',
      width: 120,
    },
    {
      title: '角色',
      dataIndex: 'role',
      key: 'role',
      render: (role: string) => (
        <Tag color={roleColorMap[role] || 'blue'}>{roleMap[role] || role}</Tag>
      ),
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      render: (status: string) => {
        const statusMap: Record<string, { label: string; color: string }> = {
          active: { label: '正常', color: 'success' },
          inactive: { label: '未激活', color: 'warning' },
          suspended: { label: '已暂停', color: 'error' },
        };
        const statusInfo = statusMap[status] || statusMap.active;
        return <Tag color={statusInfo.color}>{statusInfo.label}</Tag>;
      },
    },
    {
      title: '企业名称',
      dataIndex: 'companyName',
      key: 'companyName',
      width: 150,
      render: (name: string) => name || '-',
    },
    {
      title: '确权状态',
      dataIndex: 'verificationStatus',
      key: 'verificationStatus',
      render: (status: string) => {
        const statusMap: Record<string, { label: string; color: string }> = {
          none: { label: '未申请', color: 'default' },
          pending: { label: '待审核', color: 'warning' },
          approved: { label: '已通过', color: 'success' },
          rejected: { label: '已拒绝', color: 'error' },
        };
        const statusInfo = statusMap[status] || statusMap.none;
        return <Tag color={statusInfo.color}>{statusInfo.label}</Tag>;
      },
    },
    {
      title: '推荐码',
      dataIndex: 'referralCode',
      key: 'referralCode',
      width: 100,
    },
    {
      title: '下级数量',
      dataIndex: 'childrenCount',
      key: 'childrenCount',
      width: 80,
      render: (count: number) => count || 0,
    },
    {
      title: '一级推荐人',
      key: 'parentInfo',
      width: 150,
      render: (_: any, record: any) => {
        if (record.level === 1) return '-';
        return record.parentInfo ? (
          <div>
            <div>{record.parentInfo.nickname}</div>
            <div style={{ fontSize: '12px', color: '#666' }}>
              {record.parentInfo.phone}
            </div>
          </div>
        ) : '-';
      },
    },
    {
      title: '注册时间',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 150,
      render: (date: string) => date ? new Date(date).toLocaleString() : '-',
    },
  ];

  const handleReferralsTableChange = (pagination: any) => {
    fetchReferralsByPhone(pagination.current, pagination.pageSize);
  };

  const tabItems = [
    {
      key: 'tree',
      label: (
        <span>
          <TeamOutlined />
          推荐官层级
        </span>
      ),
      children: (
        <Card
          title="推荐官层级关系"
          extra={
            <Button icon={<ReloadOutlined />} onClick={fetchReferralTree}>
              刷新
            </Button>
          }
        >
          {loading ? (
            <div style={{ textAlign: 'center', padding: '40px' }}>加载中...</div>
          ) : treeData.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '40px', color: '#999' }}>
              暂无推荐官数据
            </div>
          ) : (
            <Tree
              showLine
              treeData={treeDataFormatted}
              defaultExpandAll
            />
          )}
        </Card>
      ),
    },
    {
      key: 'search',
      label: (
        <span>
          <SearchOutlined />
          被推荐人查询
        </span>
      ),
      children: (
        <Card title="根据推荐人手机号查询被推荐人">
          <div style={{ marginBottom: '16px' }}>
            <Row gutter={[16, 16]} align="middle">
              <Col xs={24} sm={12} md={6}>
                <Search
                  placeholder="输入推荐人手机号"
                  value={searchPhone}
                  onChange={(e) => setSearchPhone(e.target.value)}
                  onSearch={() => fetchReferralsByPhone()}
                  enterButton={<SearchOutlined />}
                  style={{ width: '100%' }}
                />
              </Col>
              <Col xs={24} sm={12} md={4}>
                <Select
                  placeholder="筛选层级"
                  value={levelFilter}
                  onChange={(value) => setLevelFilter(value)}
                  allowClear
                  style={{ width: '100%' }}
                  options={[
                    { value: undefined, label: '全部' },
                    { value: '1', label: '一级' },
                    { value: '2', label: '二级' },
                  ]}
                />
              </Col>
              <Col xs={24} sm={12} md={4}>
                <Button
                  icon={<ReloadOutlined />}
                  onClick={() => fetchReferralsByPhone()}
                  loading={searchLoading}
                >
                  查询
                </Button>
              </Col>
            </Row>
          </div>

          {searchLoading ? (
            <div style={{ textAlign: 'center', padding: '40px' }}>
              <Spin />
            </div>
          ) : searchResult ? (
            <>
              <Card
                size="small"
                title="推荐人信息"
                style={{ marginBottom: '16px' }}
              >
                <Descriptions column={4} size="small">
                  <Descriptions.Item label="手机号">{searchResult.phone}</Descriptions.Item>
                  <Descriptions.Item label="昵称">{searchResult.nickname}</Descriptions.Item>
                  <Descriptions.Item label="角色">
                    <Tag color={roleColorMap[searchResult.role] || 'blue'}>
                      {roleMap[searchResult.role] || searchResult.role}
                    </Tag>
                  </Descriptions.Item>
                  <Descriptions.Item label="推荐码">{searchResult.referralCode}</Descriptions.Item>
                  <Descriptions.Item label="企业名称">{searchResult.companyName || '-'}</Descriptions.Item>
                  <Descriptions.Item label="确权状态">
                    <Tag color={
                      searchResult.verificationStatus === 'approved' ? 'success' :
                      searchResult.verificationStatus === 'pending' ? 'warning' :
                      searchResult.verificationStatus === 'rejected' ? 'error' : 'default'
                    }>
                      {searchResult.verificationStatus === 'approved' ? '已通过' :
                       searchResult.verificationStatus === 'pending' ? '待审核' :
                       searchResult.verificationStatus === 'rejected' ? '已拒绝' : '未申请'}
                    </Tag>
                  </Descriptions.Item>
                </Descriptions>
              </Card>

              <Row gutter={[16, 16]} style={{ marginBottom: '16px' }}>
                <Col span={6}>
                  <Statistic
                    title="一级被推荐人"
                    value={searchResult.stats?.totalLevel1 || 0}
                    suffix="人"
                  />
                </Col>
                <Col span={6}>
                  <Statistic
                    title="二级被推荐人"
                    value={searchResult.stats?.totalLevel2 || 0}
                    suffix="人"
                  />
                </Col>
                <Col span={6}>
                  <Statistic
                    title="活跃用户"
                    value={(searchResult.stats?.activeLevel1 || 0) + (searchResult.stats?.activeLevel2 || 0)}
                    suffix="人"
                  />
                </Col>
                <Col span={6}>
                  <Statistic
                    title="已确权用户"
                    value={(searchResult.stats?.verifiedLevel1 || 0) + (searchResult.stats?.verifiedLevel2 || 0)}
                    suffix="人"
                  />
                </Col>
              </Row>

              <Table
                columns={referralsColumns}
                dataSource={referralsList}
                rowKey="id"
                loading={searchLoading}
                pagination={{
                  ...referralsPagination,
                  showSizeChanger: true,
                  showQuickJumper: true,
                  showTotal: (total) => `共 ${total} 条`,
                }}
                onChange={handleReferralsTableChange}
                scroll={{ x: 1200 }}
              />
            </>
          ) : (
            <div style={{ textAlign: 'center', padding: '40px', color: '#999' }}>
              请输入推荐人手机号进行查询
            </div>
          )}
        </Card>
      ),
    },
    {
      key: 'earnings',
      label: (
        <span>
          <DollarOutlined />
          推荐官收益
        </span>
      ),
      children: (
        <Card
          title="推荐官收益情况"
          extra={
            <Button icon={<ReloadOutlined />} onClick={() => fetchEarnings()}>
              刷新
            </Button>
          }
        >
          <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
            <Col span={24}>
              <Statistic
                title="总收益"
                value={totalEarnings}
                prefix="¥"
                precision={2}
                valueStyle={{ color: '#1890ff' }}
              />
            </Col>
          </Row>

          <div style={{ marginBottom: '16px' }}>
            <Row gutter={[16, 16]} align="middle">
              <Col xs={24} sm={12} md={6}>
                <Search
                  placeholder="搜索用户ID"
                  onSearch={(value) => setUserIdFilter(value ? parseInt(value) : undefined)}
                  style={{ width: '100%' }}
                />
              </Col>
              <Col xs={24} sm={12} md={6}>
                <RangePicker
                  placeholder={['开始日期', '结束日期']}
                  onChange={(dates) => setDateRange(dates as [dayjs.Dayjs, dayjs.Dayjs] | null)}
                  style={{ width: '100%' }}
                />
              </Col>
            </Row>
          </div>

          <Table
            columns={earningsColumns}
            dataSource={earnings}
            rowKey="id"
            loading={earningsLoading}
            pagination={{
              ...earningsPagination,
              showSizeChanger: true,
              showQuickJumper: true,
              showTotal: (total) => `共 ${total} 条`,
            }}
            onChange={handleEarningsTableChange}
            scroll={{ x: 1500 }}
          />
        </Card>
      ),
    },
  ];

  return (
    <AuthGuard>
      <div>
        <Tabs defaultActiveKey="tree" items={tabItems} />
      </div>
    </AuthGuard>
  );
};

export default ReferralManagement;